<template>
  <v-dialog
    :value="value"
    :width="width"
    fullscreen
    transition="dialog-bottom-transition"
    eager
    persistent
    no-click-animation
    :retain-focus="false"
    @input="$emit('input', $event)"
  >
    <!-- Fullscreen Modal -->
    <div
      :style="{height: '100%'}"
      class="sn-modal-fs sn-background-gray-01 d-flex"
      data-cy="my-account-modal"
    >
      <v-container
        class="fixed text-wrap"
        :style="{width: '25%'}"
      >
        <v-row>
          <div
            class="modal-title-button mt-6 ml-12"
            @click="onClose"
          >
            <v-icon data-cy="exit-modal-btn">
              $snArrowLeft
            </v-icon>
            <span>Exit</span>
          </div>
        </v-row>
        <v-row
          no-gutters
          class="pl-5 my-7"
        >
          <v-col cols="12">
            <div class="modal-title mr-6 ml-6 mb-10">
              <h1
                class="sn-h1"
                data-cy="my-account-modal-title"
              >
                My account
              </h1>
            </div>
            <v-tabs
              class="pl-2"
              :value="tab"
              vertical
              hide-slider
              background-color="transparent"
              active-class="selected-tab"
              @change="handleTabChange"
            >
              <v-tab
                data-cy="profile-info-tab"
                :ripple="false"
              >
                Profile info
              </v-tab>
              <v-tab
                data-cy="change-password-tab"
                :ripple="false"
              >
                Change password
              </v-tab>
              <v-tab
                data-cy="share-preferences-tab"
                :ripple="false"
              >
                Share preferences
              </v-tab>
              <v-tab
                data-cy="recent-activity-tab"
                :ripple="false"
              >
                Recent activity
              </v-tab>
            </v-tabs>
          </v-col>
        </v-row>
      </v-container>
      <v-container
        fluid
        class="my-0 overflow-y-auto"
      >
        <v-row>
          <v-col
            cols="12"
            class="px-14 py-2"
          >
            <v-tabs-items v-model="tab">
              <v-tab-item
                v-for="tab in tabs"
                :key="tab"
                :transition="false"
                :reverse-transition="false"
              >
                <v-card
                  class="rounded-lg my-16 pa-5 my-account-card"
                >
                  <profile-info
                    v-if="tab === 'Profile info'"
                    :current-user="currentUser"
                  />
                  <recent-activity v-if="tab === 'Recent activity'" />
                  <change-password v-if="tab === 'Change password'" />
                  <share-preferences
                    v-if="tab === 'Share preferences'"
                    :reload-shared-contacts="reloadSharedContacts"
                  />
                </v-card>
              </v-tab-item>
            </v-tabs-items>
          </v-col>
        </v-row>
      </v-container>
    </div>
  </v-dialog>
</template>

<script>
import RecentActivity from '~/components/portals/closing/myAccount/recentActivity.vue'
import changePassword from '~/components/portals/closing/myAccount/changePassword.vue'
import profileInfo from '~/components/portals/closing/myAccount/profileInfo.vue'
import sharePreferences from '~/components/portals/closing/myAccount/sharePreferences.vue'

export default {
  name: 'MyAccount',

  components: {
    profileInfo,
    changePassword,
    RecentActivity,
    sharePreferences
  },
  props: {
    value: {
      type: Boolean,
      default: false,
      required: true
    },
    initialTab: {
      type: Number,
      default: 0,
      required: false
    },
    currentUser: {
      type: Object,
      default: null,
      required: false
    }
  },

  data () {
    return {
      dialog: this.value,
      tab: this.initialTab,
      tabs: ['Profile info', 'Change password', 'Share preferences', 'Recent activity']
    }
  },

  computed: {
    reloadSharedContacts () {
      return this.tab === 2
    },
    width () {
      return this.$vuetify.breakpoint.lgAndUp ? '70%' : '80%'
    }
  },

  watch: {
    dialog (value, oldValue) {
      if (value) {
        // disable root scrollbar when modal is opened
        document.querySelector('html').style.overflowY = 'hidden'
      } else {
        // re-enable the root scrollbar when modal is closed
        document.querySelector('html').style.overflowY = 'auto'
      }
      if (value !== oldValue) {
        this.$emit('input', value)
      }
    },
    value (val) {
      this.dialog = val
    },
    initialTab (val) {
      this.tab = val
    }
  },

  methods: {
    onClose () {
      this.tab = 0
      this.$router.replace({ query: {} })
      this.$emit('input', false)
    },
    handleTabChange (val) {
      this.tab = val
      this.$router.replace({ query: { tab: val } })
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~/assets/css/snui';

::v-deep {
  .selected-tab {
    font-weight: $font-weight-bold !important;
    font-size: 17px !important;
    line-height: 24px !important;
    text-decoration: underline;
    text-decoration-color: $sn-brand-03;
    text-underline-offset: 6px;
    color: $sn-primary-03;
  }

  .v-tab {
    display: flex;
    justify-content: flex-start;
    font-family: $font-family-primary;
    font-weight: $font-weight-regular;
    font-size: 17px;
    line-height: 24px;
    letter-spacing: normal;
    color: $sn-primary-03 !important;
    padding-bottom: 2px;
    text-transform: none;
  }

  .v-tab:hover {
    opacity: 0.7;
  }

  .theme--light.v-tabs .v-tab--active:hover  {
    opacity: 1;
  }

  .theme--light.v-tabs .v-tab:hover::before {
    opacity: 0 !important;
  }

  .theme--light.v-tabs .v-tab--active:focus::before  {
    opacity: 0 !important;
  }

  .v-tabs--vertical > .v-tabs-bar .v-tab {
    height: 40px;
  }

  .theme--light.v-tabs-items {
    background-color: transparent !important;
  }

    z-index: 1000;
  .sn-modal {
    display: flex;

    &:not(.v-dialog--fullscreen) {
      border-radius: 16px;
    }

    /*** Fullscreen styles ***/
    &.v-dialog--fullscreen .sn-modal-fs {
      &--container {
        max-height: calc(100vh - 120px);
        border-radius: 16px !important;

        .v-data-table__wrapper {
          max-height: calc(100vh - 370px);
          overflow: auto;

          .v-data-table-header .text-start {
            box-shadow: none !important;
          }
        }
      }
      h1 { text-align: center; }
      h2 { text-align: center; }
    }
    /*** End fullscreen styles ***/

    &-title {
      word-break: break-word;
    }

    .sn-h3 {
      font-size: 26px !important;
      font-weight: $font-weight-semi-bold !important;

      .v-icon {
        color: $sn-primary-03;
      }
    }
  }
}

.my-account-card {
  box-shadow: $sn-shadow-depth-1 !important;
}

@media (max-width: 979px) {
  ::v-deep .sn-modal {
    margin: 0 !important;
    height: 100vh;
    max-height: 100% !important;
    border-radius: 0;
    max-width: 100% !important;
    width: 100% !important;
    overflow: auto;

    .v-card {
      max-height: 100% !important;
      height: 100vh;
      min-width: 100% !important;

      .v-card__title {
        width: 100%;

        .v-icon {
          -webkit-appearance: none;
        }
      }
      .v-card__text {
        width: 100%;
      }
    }
  }
}
.modal-title-button {
  font-family: $font-family-primary;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex: 0 64px;
  text-align: center;
  align-self: center;
  span {
    font-weight: 600;
  }
}

.modal-title {
  flex-grow: 1;
}
</style>
